import { Component, Inject, Injector, OnInit } from '@angular/core';
import { MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RadMeter, RadMeterModel } from '../rad-meters';
import { RadMeterModelDetailsComponent } from './rad-meter-model-details/rad-meter-model-details.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { RadMetersService } from '../services/rad-meters.service';
import { BaseComponent } from 'src/app/common/base/base.component';

@Component({
  selector: 'rad-meter-models',
  templateUrl: './rad-meter-models.component.html',
  styleUrls: ['./rad-meter-models.component.scss']
})
export class RadMeterModelsComponent extends BaseComponent implements OnInit {
  public valueFilter = '';
  public dataSource: MatTableDataSource<RadMeterModel> = new MatTableDataSource<RadMeterModel>();
  public showInactive!: boolean;
  public loading = false;
  public radMetersModelList?: RadMeterModel[];
  public displayedColumns: string[] = ['name', 'description', 'status', 'actions'];
  public createAvailable!: boolean;
  public selectedRowIndex = -1;
  public pending = false;

  constructor(
    protected override injector: Injector,
    private dialogRef: MatDialogRef<RadMeterModelsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RadMeter,
    private _service: RadMetersService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.clear();
    this.loadMeterModels();
    this.createAvailable = true;
  }

  clear() {
    this.showInactive = true;
    this.selectedRowIndex = 0;
    this.dataSource = new MatTableDataSource();
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  async loadMeterModels(event?: any) {
    this.loading = true;
    this.valueFilter = '';
    await this._service.getRadMeterModels().toPromise().then(result => {
      if (!result) {
        result = [];
      }
      this.radMetersModelList = result;
      if (!this.showInactive) {
        this.dataSource = new MatTableDataSource<RadMeterModel>(result.filter(x => x.status == 1));
      } else {
        this.dataSource = new MatTableDataSource<RadMeterModel>(result);
      }
    }).finally(() => this.loading = false);
  }

  refreshMeterModels(event?: any) {
    this.loadMeterModels();
    this.selectedRowIndex = -1;
  }

  showMetersModelsInactive() {
    this.loadMeterModels();
  }

  sortData(sort: Sort) {
    const data = this.radMetersModelList;
    let sortedData: RadMeterModel[];
    if (!sort.active || sort.direction === '') {
      sortedData = data ?? [];
      return;
    }
    sortedData = data?.sort((a, b) => {
      const isDesc = sort.direction === 'desc';
      switch (sort.active) {
        case 'name': return compare(a.name, b.name, isDesc);
        case 'description': return compare(a.description, b.description, isDesc);
        case 'status': return compare(a.status, b.status, isDesc);
        default: return 0;
      }
    }) ?? [];
    this.dataSource = new MatTableDataSource(sortedData);
  }

  highlight(row: any) {
    if (!this.pending) {
      this.selectedRowIndex = row.id;
    }
  }

  getPosition(row: any): number {
    const i = this.dataSource.data.findIndex(r => {
      return r.id == row.id;
    });
    return i;
  }

  getColor(radMeterModel: RadMeterModel): string {
    if (radMeterModel.status == 0) {
      return '#D8D8D8';
    }
    else {
      return '#000000';
    }
  }

  createMeterModel() {
    this.selectedRowIndex = 0;
    const newRadMeterModel: RadMeterModel = {} as RadMeterModel;
    newRadMeterModel.id = 0;
    newRadMeterModel.description = '';
    newRadMeterModel.code = '';
    newRadMeterModel.status = 1;
    newRadMeterModel.name = '';
    newRadMeterModel.type = 0;
    this.openDetailsMeterModel(newRadMeterModel);
  }

  openDetailsMeterModel(newRadMeterModel: RadMeterModel) {
    const dataDialog = newRadMeterModel;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = dataDialog;
    dialogConfig.minWidth = '35vw';
    dialogConfig.maxHeight = '85%';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    const dialogRef = this.dialog.open(RadMeterModelDetailsComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(data => {
      if (data != undefined) {
        this.loading = true;
        this.refreshMeterModels(data);
        if (data == 'Create') {
          this.alert.success(this.getMessage('RAD_Meters_Catalog_Create_Model_Succeed')?.description);
        }
        else if (data == 'Update') {
          this.alert.success(this.getMessage('RAD_Meters_Catalog_Update_Model_Succeed')?.description);
        }
      }
    });
  }

  close() {
    this.dialogRef.close();
  }

  async deleteMeterModel(element: RadMeterModel) {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('RAD_Meters_Catalog_Confirm_Delete_Model')?.description,
        icon: 'stop'
      }
    });
    confirm.afterClosed().toPromise().then(async data => {
      if (data && element.id) {
        this.loading = true;
        await this._service.deleteRadMeterModel(element.id).toPromise().then(() => {
          this.alert.success(this.getMessage('RAD_Meters_Catalog_Delete_Model_Succeed')?.description);
        }).catch(() => {
          this.alert.error('Error: The meter model was not deleted.');
        });
      }
    }, error => {
    }).finally(() => {
      this.loading = false;
      this.refreshMeterModels(true);
    });
  }

  EditMeterModel(element: RadMeterModel) {
    this.openDetailsMeterModel(element);
  }
}
function compare(a?: number | string, b?: number | string, isAsc?: boolean) {
  if (a && b)
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  return 1;
}
